import { FC, FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useAppForm } from '@new/common/hooks/form';
import { formOptions } from '@tanstack/react-form';
import z from 'zod';
import { emailValidation } from '../../schemas/validations';
import { useForgotPasswordMutation } from '@teachur/http-client';
import { getErrorMessage } from '@new/lib';
import { toast } from 'sonner';

const forgotSchema = z.object({
  email: emailValidation,
});

const forgotFormOpts = formOptions({
  defaultValues: {
    email: '',
  },
});

export const ForgotPasswordForm: FC = () => {
  const { mutateAsync: forgotPassword } = useForgotPasswordMutation({
    onSuccess: () => {
      toast.success('Reset link sent to your email');
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Failed to send reset link');
      toast.error(message);
    },
  });

  const form = useAppForm({
    ...forgotFormOpts,
    validators: {
      onChange: ({ value }) => {
        const parsed = forgotSchema.safeParse(value);

        if (parsed.success) return { fields: {} };

        const errors: Record<string, string> = {};
        parsed.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (typeof field === 'string') errors[field] = issue.message;
        });

        return { fields: errors };
      },

      onSubmit: ({ value }) => {
        const parsed = forgotSchema.safeParse(value);
        if (!parsed.success) {
          const errors: Record<string, string> = {};

          parsed.error.issues.forEach((issue) => {
            const field = issue.path[0];
            if (typeof field === 'string') {
              errors[field] = issue.message;
            }
          });

          return { fields: errors };
        }

        forgotPassword(parsed.data);
      },
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <>
      <div className="w-full flex flex-col justify-between items-center gap-10">
        <div className="flex flex-col justify-center items-center gap-1">
          <h1 className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h3)]! md:[font-size:var(--fs-h2)]! xl:[font-size:var(--fs-h1)]! leading-(--lh-h)! font-bold! m-0! text-(--neutral-black-NB1000)!">
            Reset your password
          </h1>
          <p className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-p3)]! text-center! leading-(--lh-p)! font-medium! m-0! text-(--neutral-black-NB900)!">
            We'll send you a reset link to your email.
          </p>
        </div>
        <form
          data-test-id="forgot-password-form"
          onSubmit={onSubmit}
          className="w-full flex flex-col gap-3"
        >
          <div className="w-full flex-1 flex flex-col gap-1">
            <form.AppField
              name="email"
              children={(field) => (
                <field.Email label="Email" placeholder="Enter your email address" />
              )}
            />
          </div>

          <form.AppForm>
            <form.SubmitButton label="Get a code" />
          </form.AppForm>
        </form>
      </div>
      <div className="flex items-center gap-1 self-center">
        <p className="[font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--neutral-white-NW600) m-0!">
          Don't have an account?
        </p>
        <Link
          to="/signup"
          className="bold underline! [font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--primary-O900)!"
        >
          Create an account
        </Link>
      </div>
    </>
  );
};
